<script setup lang="ts">
import useVuelidate from '@vuelidate/core'

import LoadingComponent from '~/components/loadingComponent.vue'
import { useFormBuilder } from '~/layers/forms/composables/useFormBuilder'
import { Contract, CustomerTypeEnum, Form } from '~/layers/forms/models'
import { UTM } from '~/layers/forms/models/utm'
import type {
  CoverageComparatorListExpose,
  CoverageListExpose,
} from '~/layers/forms/types/coverageComparatorDefinedExpose'
import type { SubmitResult } from '~/layers/forms/types/submitResult'

definePageMeta({
  layout: 'focus',
})

const { user, loggedIn } = useUserSession()

const utm = UTM.load({
  source: useRouteQuery<string | null>('utm_source', null).value,
  medium: useRouteQuery<string | null>('utm_medium', null).value,
  campaign: useRouteQuery<string | null>('utm_campaign', null).value,
})

const model = ref<Contract>(Contract.load({ utm }, user.value))
const containerRef = ref<HTMLElement | null>(null)
const productCode = useRouteParams<string | null>('code', null)

const { RemoteConfigurationStore, LocalConfigurationStore } = stores()

const { constraints } = useContractConstraints()

const productData = computed(() =>
  LocalConfigurationStore.value?.productsInfo?.find((p) => p.code === productCode.value)
)

if (!productData.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Página no encontrada',
  })
}

const associatedCompanies = computed(() => LocalConfigurationStore.value?.associatedCompanies ?? [])

const {
  formConfig,
  activeStep,
  continueToStep,
  isLastStep,
  status: formBuilderStatus,
  applyConfiguration,
} = useFormBuilder(productCode.value)

const confirm = useConfirm()
const toast = useToast()
const focus = useFocus()

const insurable = useInsurable({
  product: computed(() => RemoteConfigurationStore.value?.ramoEnum),
  contract: model,
})

const { submit: sendLead } = useLead({
  contract: model,
  insurable: insurable,
})

const v$ = useVuelidate()

const currentStepIsValid = () => {
  const formsToValidate = formConfig.value?.[activeStep.value]?.forms

  formsToValidate?.forEach(async (form) => await v$.value?.[form]?.$validate())

  const invalidForm = formsToValidate?.find((form) => v$.value?.[form]?.$invalid)

  if (invalidForm) {
    const element = document.getElementById(`${invalidForm}-form`)
    if (element) focus({ target: element })
    return false
  }
  return true
}

const selectedCompany = computed(() =>
  associatedCompanies.value.find((c) => {
    switch (RemoteConfigurationStore.value?.initBy) {
      case 'ByProducto':
        return c.argentoInsuranceId === RemoteConfigurationStore.value.emisor?.id
      case 'BySubRamo':
        return c.argentoInsuranceId === model.value.coverage.argentoInsuranceCompanyId
    }
  })
)

const currentStepHasCoverageForm = computed(() =>
  formConfig.value?.[activeStep.value]?.forms.some((form) => form === Form.coverage)
)

const process = computed(() => {
  let message = 'Cargando...'
  if (['pending', 'idle'].includes(formBuilderStatus.value)) message = 'Preparando formulario...'
  if (issueStatus.value === 'pending') message = 'Procesando solicitud, espere unos minutos...'

  return {
    loading:
      ['pending', 'idle'].includes(formBuilderStatus.value) || issueStatus.value === 'pending',
    message,
  }
})

const coverageListComponentInstance = templateRef<CoverageListExpose[]>('coverage-list')

const coverageQuotationsStatus = computed(() =>
  coverageListComponentInstance.value?.[0]?.getQuotationsStatus()
)

const coverageComparatorComponentInstance =
  templateRef<CoverageComparatorListExpose[]>('coverage-comparator')

const coverageComparatorQuotationsStatus = computed(() =>
  coverageComparatorComponentInstance.value?.[0]?.getQuotationsStatus()
)

const fetchQuotations = (selectedOnly: boolean = false) => {
  coverageComparatorComponentInstance.value?.[0]?.fetchQuotations({ selectedOnly })
}

const setIssueError = () => {
  const reason = issueError.value?.data?.code ?? 'PROCESS_ERROR'
  submitResult.value = {
    result: 'FAILED',
    reason,
  }
}

const {
  execute: issue,
  data: issueResponse,
  status: issueStatus,
  error: issueError,
} = useSiteApi('/api/Processes/Contract/Issue', {
  method: 'POST',
  body: computed(() =>
    Contract.map.toProcessBuyProductRequestModel({
      model: model.value,
      userId: user.value?.id,
      anonymous: loggedIn.value === false,
    })
  ),
  watch: false,
  immediate: false,
  onResponseError: setIssueError,
})

const coverageStatusMesages: Partial<Record<Exclude<CoveragePlanStatus, 'quoted'>, string>> = {
  error: 'Hubo un error al recalcular el monto de la cobertura del seguro. Por favor reintentar.',
  pending:
    'Estamos recalculado el monto de la cobertura del seguro. Por favor espera antes de continuar.',
}

const nextStep = async () => {
  if (
    !!coverageComparatorQuotationsStatus.value?.selectedStatus &&
    coverageComparatorQuotationsStatus.value.selectedStatus !== 'success'
  ) {
    toast.add({
      severity: 'info',
      summary: 'Cobertura',
      detail: coverageStatusMesages[coverageComparatorQuotationsStatus.value.selectedStatus],
      life: 5000,
    })
    return
  }

  if (!currentStepIsValid()) return

  continueToStep()
  if (containerRef.value) focus({ target: containerRef.value })
  submitLead()
}

const previusStep = () => {
  continueToStep('previous')
  if (containerRef.value) focus({ target: containerRef.value })
}

const submitLead = (
  { processFinalized }: { processFinalized?: boolean } = { processFinalized: false }
) => {
  if (processFinalized) {
    sendLead('Completado')
    return
  }

  const currentStage = computed<SiteApiSchema<'OportunidadEtapa'>>(() => {
    const previousStep = activeStep.value - 1
    const stage =
      formConfig.value[isLastStep.value ? activeStep.value : previousStep]?.argantoInsuranceStage ??
      'NoDefinido'

    return stage
  })

  sendLead(currentStage.value)
}

const { cloned: submitResult, sync: resetSubmitResult } = useCloned<SubmitResult>({})

const submit = async () => {
  try {
    await issue()

    if (issueResponse.value?.responseCode === '00') {
      submitResult.value.result = 'SUCCESS'
      submitLead({ processFinalized: true })
    } else {
      setIssueError()
    }
  } catch {
    setIssueError()
  }
}

const submitConfirm = async () => {
  if (!currentStepIsValid()) return

  confirm.require({
    message: 'Está seguro de que desea continuar?',

    acceptProps: {
      label: 'Continuar',
    },
    rejectProps: {
      label: 'Cancelar',
    },
    accept: submit,
  })
}

onBeforeRouteLeave(async (to, from, next) => {
  LocalConfigurationStore.value = null
  return next()
})

onMounted(async () => {
  await applyConfiguration()
  model.value = Contract.load({ utm }, user.value)
})
</script>

<template>
  <div
    class="flex-grow flex flex-col"
    :class="{
      'justify-center': submitResult.result || formBuilderStatus !== 'success',
    }"
  >
    <LoadingComponent
      :is-loading="process.loading"
      :message="process.message"
    >
      <div
        ref="containerRef"
        class="bg-dark flex flex-col w-full gap-4 rounded-2xl p-4 xl:p-8"
      >
        <FormsGlobalProductHeader
          v-if="productCode"
          :product-code
          :argento-insurance-company-id="selectedCompany?.argentoInsuranceId"
        />

        <PvDivider
          v-if="
            !productCode ||
            formBuilderStatus !== 'success' ||
            submitResult.result ||
            process.loading
          "
        />

        <FormsGlobalBlocker
          :submit-result="submitResult"
          :reset-submit-result-function="resetSubmitResult"
        />

        <StepperComponent
          v-if="productCode && formBuilderStatus === 'success'"
          v-show="!submitResult.result && !process.loading"
          v-model="activeStep"
          :steps="formConfig"
          submit-label="Finalizar"
          emited-navigation
          :finalize-acction="false"
          @next-step="nextStep"
          @previous-step="previusStep"
          @end="submitConfirm"
        >
          <template
            v-for="(_, key) in formConfig"
            :key="key"
            #[`step-${key}`]="{ data }"
          >
            <div class="flex flex-col gap-6 md:gap-10">
              <template
                v-for="form in data.forms"
                :key="form"
              >
                <template v-if="form === Form.coverage">
                  <FormsCoverageComparatorList
                    v-if="RemoteConfigurationStore?.initBy === 'BySubRamo'"
                    ref="coverage-comparator"
                    v-model="model"
                    :insurable
                    :is-user-chosing="currentStepHasCoverageForm"
                    @selected="nextStep()"
                  />
                  <FormsCoverageList
                    v-else
                    ref="coverage-list"
                    v-model="model"
                    :insurable
                    :is-user-chosing="currentStepHasCoverageForm"
                    @selected="nextStep()"
                  />
                </template>

                <FormsCustomerForm
                  v-if="form === Form.customer"
                  v-model="model.customer"
                  :customer-as-insured="model.insured.customerAsInsured"
                  :insurable
                >
                  <template #aditional-fields>
                    <FormsPepFields
                      v-if="RemoteConfigurationStore?.form?.requiereCumplimiento"
                      v-model="model.pep"
                      :validation-scope="Form.customer"
                    />
                  </template>
                </FormsCustomerForm>

                <FormsContactForm
                  v-if="form === Form.contact && RemoteConfigurationStore?.form?.capturaContactos"
                  v-model="model.contact"
                />

                <FormsInsuredForm
                  v-if="form === Form.insured"
                  v-model="model.insured"
                  :customer="model.customer"
                  :insurable
                >
                  <template #aditional-fields>
                    <FormsPepFields
                      v-if="RemoteConfigurationStore?.form?.requiereCumplimiento"
                      v-model="model.pep"
                      :validation-scope="Form.customer"
                    />
                  </template>
                </FormsInsuredForm>

                <FormsVehicleForm
                  v-if="form === Form.vehicle || form === Form.moto"
                  v-model="model.vehicle"
                  :pre-filled="productData?.useComparator"
                  :for-moto="form === Form.moto"
                  :use-version="selectedCompany?.quoteWithVersion"
                  :argento-insurance-company-id="model.coverage.argentoInsuranceCompanyId"
                  :argento-insurance-product-id="model.coverage.argentoInsuranceProductId"
                  :argento-insurancebroker-product-id="
                    model.coverage.argentoInsuranceCoverageRequestData?.brokerProductoId
                  "
                />

                <FormsTravelForm
                  v-if="form === Form.travel"
                  v-model="model.travel"
                  :insured="model.insured"
                />

                <FormsPetForm
                  v-if="form === Form.pet"
                  v-model="model.pet"
                />

                <FormsAddressForm
                  v-if="form === Form.address && RemoteConfigurationStore?.form?.capturaDireccion"
                  v-model="model.address"
                  :argento-insurance-company-id="model.coverage.argentoInsuranceCompanyId"
                  :argento-insurance-product-id="model.coverage.argentoInsuranceProductId"
                  :argento-insurancebroker-product-id="
                    model.coverage.argentoInsuranceCoverageRequestData?.brokerProductoId
                  "
                />

                <FormsRealStateAddressFrom
                  v-if="form === Form.realStateAddress"
                  v-model="model.realState.address"
                  :customer-address="model.address"
                  :argento-insurance-company-id="model.coverage.argentoInsuranceCompanyId"
                  :argento-insurance-product-id="model.coverage.argentoInsuranceProductId"
                  :argento-insurancebroker-product-id="
                    model.coverage.argentoInsuranceCoverageRequestData?.brokerProductoId
                  "
                />

                <FormsDeviceForm
                  v-if="form === Form.device"
                  v-model="model.device"
                />

                <FormsBiometricsForm
                  v-if="form === Form.biometrics && constraints?.captureBiometrics"
                  v-model="model.biometrics"
                />

                <FormsInsuredAmountForm
                  v-if="form === Form.insuredAmount"
                  v-model="model.insuredAmount"
                />

                <FormsSpecialConditionsForm
                  v-if="form === Form.specialConditions && constraints?.captureSpecialConditions"
                  v-model="model.specialConditions"
                />

                <FormsAlternateVehicleForm
                  v-if="form === Form.alternateVehicle"
                  v-model="model.alternativeVehicle"
                />

                <FormsRealStateForm
                  v-if="form === Form.realState"
                  v-model="model.realState"
                />

                <FormsIdentificationForm
                  v-if="form === Form.identification"
                  v-model="model.customer.identification"
                  :entity-type="CustomerTypeEnum.Person.code"
                />

                <FormsAuthForm
                  v-if="form === Form.auth"
                  v-model="model.contact"
                  :process-info="model"
                />

                <FormsBeneficiaryCrud
                  v-if="form === Form.beneficiaries"
                  v-model="model.beneficiaries"
                />

                <FormsResumeSumary
                  v-if="form === Form.resume && productData"
                  v-model="model"
                  :data="model"
                  @end-process="submitConfirm()"
                />

                <FormsAditionalInfoForm
                  v-if="form === Form.aditionalInfo"
                  v-model="model.aditionalInfo"
                />

                <FormsTosForm
                  v-if="form === Form.tos"
                  v-model="model.tos"
                />
              </template>
              <template v-if="isLastStep">
                <hr class="col-span-full border-primary-100/20" />
                <PvButton
                  severity="success"
                  class="col-span-full"
                  @click="submitConfirm()"
                >
                  <span
                    class="flex items-center gap-2 py-2 text-xl font-bold uppercase md:text-2xl"
                  >
                    <span class="icon-[ph--shield-check-bold]" />
                    Finalizar
                  </span>
                </PvButton>
              </template>
              <template
                v-if="
                  ![Form.coverage, Form.resume, Form.auth].some((form) =>
                    data.forms.includes(form)
                  ) && model.coverage.argentoInsuranceCompanyId
                "
              >
                <PvMessage
                  v-if="coverageComparatorQuotationsStatus?.selectedStatus === 'error'"
                  severity="error"
                  class="backdrop-blur-xl sticky bottom-5 flex w-full justify-center py-2"
                >
                  <div class="flex flex-col gap-2">
                    <div class="text-center">
                      Hubo un error al recalcular el monto de la cobertura del seguro.
                    </div>
                    <PvButton
                      severity="danger"
                      class="w-full"
                      @click="fetchQuotations(true)"
                    >
                      <span class="icon-[ph--arrow-counter-clockwise-bold]" />
                      Reintentar
                    </PvButton>
                  </div>
                </PvMessage>
                <FormsCoverageResume
                  v-else
                  :coverage="model.coverage"
                  card-layout="horizontal"
                  bg="light"
                  class="sticky bottom-5"
                  :content-layout="$viewport.isGreaterThan('sm') ? 'horizontal' : 'vertical'"
                  :status="
                    coverageQuotationsStatus ?? coverageComparatorQuotationsStatus?.selectedStatus
                  "
                />
              </template>
            </div>
          </template>
        </StepperComponent>
        <FormsGlobalAssistancePanel
          v-if="formBuilderStatus === 'error'"
          heading="Hubo un problema al cargar el formulario del producto."
          text="Favor contactar a soporte técnico mediante nuestros canales de contacto para recibir asistencia."
        />
      </div>
    </LoadingComponent>
  </div>
</template>
